import './LoginForm.css'; // Reuse the same CSS as the login form
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';
import tokenValidator from '../utils/tokenValidator';
import { apiClient } from '../utils/apiClient';

function PasswordReset() {
  const navigate = useNavigate();
  const location = useLocation(); // Access the current URL
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(null);
  const [tokenStatus, setTokenStatus] = useState({ loading: true, valid: false, message: '' });

  // Extract resetToken and email from the URL query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tokenFromUrl = queryParams.get('token');
    const emailFromUrl = queryParams.get('email');

    if (tokenFromUrl) setResetToken(tokenFromUrl);
    if (emailFromUrl) setEmail(emailFromUrl);
    
    // Validate token when component mounts
    if (tokenFromUrl && emailFromUrl) {
      validateToken(tokenFromUrl, emailFromUrl);
    } else {
      setTokenStatus({ 
        loading: false, 
        valid: false, 
        message: 'Invalid reset link. Missing token or email.' 
      });
    }
  }, [location.search]);
  
  const validateToken = async (token, email) => {
    try {
      const result = await tokenValidator.checkTokenExpiration(token, email);
      setTokenStatus({
        loading: false,
        valid: !result.expired,
        message: result.message,
        warning: result.warning
      });
    } catch (error) {
      setTokenStatus({
        loading: false,
        valid: false,
        message: 'Unable to validate reset link. Please request a new one.'
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }
    
    // Check password strength
    if (!passwordStrength || passwordStrength.level === 'weak') {
      setErrorMessage('Password is too weak. Please choose a stronger password.');
      return;
    }
    
    // Validate token is still valid
    if (!tokenStatus.valid) {
      setErrorMessage('Reset link has expired or is invalid. Please request a new one.');
      return;
    }

    try {
      console.log(`Resetting password for ${email} with token ${resetToken}`);
      await apiClient.post('/api/users/reset-password', { token: resetToken, email, newPassword });

      alert('Password reset successful! You can now log in with your new password.');
      navigate('/login'); // Redirect to the login page after successful reset
    } catch (error) {
      console.error('Password reset failed:', error);
      alert('Failed to reset password. Please try again.');
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
        content: 'center',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      className="NotTheBody"
    >
      <div className="wrapper">
        {tokenStatus.loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p>Validating reset link...</p>
          </div>
        ) : !tokenStatus.valid ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <h1>Invalid Reset Link</h1>
            <p style={{ color: 'red', marginBottom: '20px' }}>{tokenStatus.message}</p>
            <button 
              onClick={() => navigate('/ForgotPassword')}
              style={{ 
                padding: '10px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Request New Reset Link
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <h1>Password Reset</h1>
            {tokenStatus.warning && (
              <div style={{ 
                backgroundColor: '#fff3cd', 
                color: '#856404', 
                padding: '10px', 
                borderRadius: '4px', 
                marginBottom: '15px',
                fontSize: '14px'
              }}>
                ⚠️ {tokenStatus.message}
              </div>
            )}
            <div className="input-box">
              <input
                type="password"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <PasswordStrengthIndicator password={newPassword} onStrengthChange={setPasswordStrength} />
            </div>
            <div className="input-box">
              <input
                type="password"
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            {errorMessage && (
              <p style={{ color: 'red', textAlign: 'center' }}>{errorMessage}</p>
            )}
            <button type="submit" disabled={!passwordStrength || passwordStrength.level === 'weak'}>
              Reset Password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default PasswordReset;